import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { animateScroll } from 'react-scroll'

import { Range } from '../../inputs/Range'
import styles from './capacity.module.scss'
import { ComponentErrorBoundary } from '@/components/atomic/ComponentErrorBoundary'
import { FilterDropdown } from '@/components/search/FilterDropdown'
import {
  FILTER_CAPACITY,
  FILTER_CAPACITY_LOAD_HEIGHT,
  FILTER_CAPACITY_LOAD_HEIGHT_MAX,
  FILTER_CAPACITY_LOAD_LENGTH,
  FILTER_CAPACITY_LOAD_LENGTH_MAX,
  FILTER_CAPACITY_LOAD_WIDTH,
  FILTER_CAPACITY_LOAD_WIDTH_MAX,
  FILTER_CAPACITY_PAYLOAD,
  FILTER_CAPACITY_PAYLOAD_MAX,
} from '@/lib/constants'
import { LOADERS } from '@/lib/loaders'
import { capitaliseFirstLetter, filterLabelPrettyName } from '@/lib/utilities/search'
import { addToDataLayer } from '@/lib/utilities/vehicle'
import { storeSearchParameters } from '@/store/reducers/search'
import { selectLoaderExists } from '@/store/selectors/loader'
import { selectActiveCapacity, selectCapacitySearchFilter } from '@/store/selectors/search'

type CapacityProps = {
  isFilterOpen: boolean
  title: string
  toggleFilter: () => void
}

export const Capacity = ({ isFilterOpen = false, title, toggleFilter }: CapacityProps) => {
  const router = useRouter()
  const dispatch = useDispatch()

  const availableCapacityFilters = useSelector(selectCapacitySearchFilter)
  const activeCapacityFilters = useSelector(selectActiveCapacity)
  const isLoading = useSelector(state =>
    selectLoaderExists(state, LOADERS.fetchingSearchFilter.capacity),
  )

  const [minPayloadVal, setMinPayloadVal] = useState(
    activeCapacityFilters.payload?.min || availableCapacityFilters.payload.min,
  )
  const [maxPayloadVal, setMaxPayloadVal] = useState(
    activeCapacityFilters.payload?.max || availableCapacityFilters.payload.max,
  )
  const [minLengthVal, setMinLengthVal] = useState(
    activeCapacityFilters.loadLength?.min || availableCapacityFilters.loadLength.min,
  )
  const [maxLengthVal, setMaxLengthVal] = useState(
    activeCapacityFilters.loadLength?.max || availableCapacityFilters.loadLength.max,
  )
  const [minHeightVal, setMinHeightVal] = useState(
    activeCapacityFilters.loadHeight?.min || availableCapacityFilters.loadHeight.min,
  )
  const [maxHeightVal, setMaxHeightVal] = useState(
    activeCapacityFilters.loadHeight?.max || availableCapacityFilters.loadHeight.max,
  )
  const [minWidthVal, setMinWidthVal] = useState(
    activeCapacityFilters.loadWidth?.min || availableCapacityFilters.loadWidth.min,
  )
  const [maxWidthVal, setMaxWidthVal] = useState(
    activeCapacityFilters.loadWidth?.max || availableCapacityFilters.loadWidth.max,
  )

  useEffect(() => {
    setMinPayloadVal(
      activeCapacityFilters.payload?.min || availableCapacityFilters.payload.min,
    )

    setMaxPayloadVal(
      activeCapacityFilters.payload?.max || availableCapacityFilters.payload.max,
    )

    setMinLengthVal(
      activeCapacityFilters.loadLength?.min || availableCapacityFilters.loadLength.min,
    )

    setMaxLengthVal(
      activeCapacityFilters.loadLength?.max || availableCapacityFilters.loadLength.max,
    )

    setMinHeightVal(
      activeCapacityFilters.loadHeight?.min || availableCapacityFilters.loadHeight.min,
    )

    setMaxHeightVal(
      activeCapacityFilters.loadHeight?.max || availableCapacityFilters.loadHeight.max,
    )

    setMinWidthVal(
      activeCapacityFilters.loadWidth?.min || availableCapacityFilters.loadWidth.min,
    )

    setMaxWidthVal(
      activeCapacityFilters.loadWidth?.max || availableCapacityFilters.loadWidth.max,
    )
  }, [activeCapacityFilters, availableCapacityFilters])

  const onCapacityFinalChange = (filter: string, values: number[]) => {
    dispatch(storeSearchParameters({ filter: `${filter}Min`, value: Number(values[0]) }))
    dispatch(storeSearchParameters({ filter: `${filter}Max`, value: Number(values[1]) }))
    addToDataLayer({
      event: 'search-filter',
      'filter-event': 'add-filter',
      'filter-category': 'capacity',
      'filter-label': filter,
      'filter-value': `${values[0]} ${values[1]}`,
    })
    if (router.asPath.includes('page=')) {
      router.push('/van-leasing/search', undefined, {
        scroll: false,
      })
    }
    animateScroll.scrollToTop({
      duration: 1000,
      delay: 0,
      smooth: 'easeInOutQuart',
    })
  }

  const onCapacityChange = (filter: string, values: number[]) => {
    if (filter === FILTER_CAPACITY_PAYLOAD) {
      setMinPayloadVal(values[0])
      setMaxPayloadVal(values[1])
    }

    if (filter === FILTER_CAPACITY_LOAD_LENGTH) {
      setMinLengthVal(values[0])
      setMaxLengthVal(values[1])
    }

    if (filter === FILTER_CAPACITY_LOAD_HEIGHT) {
      setMinHeightVal(values[0])
      setMaxHeightVal(values[1])
    }

    if (filter === FILTER_CAPACITY_LOAD_WIDTH) {
      setMinWidthVal(values[0])
      setMaxWidthVal(values[1])
    }
  }

  return (
    <FilterDropdown
      title={title}
      toggleFilter={toggleFilter}
      isFilterOpen={isFilterOpen}
      hasSelectedOptions={Object.keys(activeCapacityFilters).length > 0}
      selectedOptions={
        Object.keys(activeCapacityFilters).length > 0
          ? Object.keys(activeCapacityFilters).map(
              filter =>
                `${capitaliseFirstLetter(
                  filter === FILTER_CAPACITY_PAYLOAD ? filter : filter.replace('load', ''),
                )}: ${filterLabelPrettyName(
                  FILTER_CAPACITY,
                  activeCapacityFilters[filter].min || availableCapacityFilters[filter].min,
                  filter,
                )}-${filterLabelPrettyName(FILTER_CAPACITY, activeCapacityFilters[filter].max || availableCapacityFilters[filter].max, filter)}`,
            )
          : ['All']
      }>
      <div className={styles.sliders}>
        <div className={styles.row}>
          <div className={styles.title}>
            <p>Payload</p>
          </div>
          <ComponentErrorBoundary>
            <Range
              filter={FILTER_CAPACITY}
              filteredTypeName={FILTER_CAPACITY_PAYLOAD}
              availableMinimum={
                availableCapacityFilters.payload.min >= maxPayloadVal
                  ? minPayloadVal
                  : availableCapacityFilters.payload.min
              }
              availableMaximum={
                availableCapacityFilters.payload.max >= FILTER_CAPACITY_PAYLOAD_MAX
                  ? FILTER_CAPACITY_PAYLOAD_MAX
                  : availableCapacityFilters.payload.max
              }
              activeMinimum={minPayloadVal}
              activeMaximum={maxPayloadVal}
              step={100}
              onChange={values => onCapacityChange(FILTER_CAPACITY_PAYLOAD, values)}
              onFinalChange={values => onCapacityFinalChange(FILTER_CAPACITY_PAYLOAD, values)}
              isLoading={isLoading}
            />
          </ComponentErrorBoundary>
        </div>
        <div className={styles.row}>
          <div className={styles.title}>
            <p>Load Length</p>
          </div>
          <ComponentErrorBoundary>
            <Range
              filter={FILTER_CAPACITY}
              filteredTypeName={FILTER_CAPACITY_LOAD_LENGTH}
              availableMinimum={
                availableCapacityFilters.loadLength.min >= maxLengthVal
                  ? minLengthVal
                  : availableCapacityFilters.loadLength.min
              }
              availableMaximum={
                availableCapacityFilters.loadLength.max >= FILTER_CAPACITY_LOAD_LENGTH_MAX
                  ? FILTER_CAPACITY_LOAD_LENGTH_MAX
                  : availableCapacityFilters.loadLength.max
              }
              activeMinimum={minLengthVal}
              activeMaximum={maxLengthVal}
              onChange={values => onCapacityChange(FILTER_CAPACITY_LOAD_LENGTH, values)}
              onFinalChange={values => onCapacityFinalChange(FILTER_CAPACITY_LOAD_LENGTH, values)}
              isLoading={isLoading}
            />
          </ComponentErrorBoundary>
        </div>
        <div className={styles.row}>
          <div className={styles.title}>
            <p>Load Height</p>
          </div>
          <ComponentErrorBoundary>
            <Range
              filter={FILTER_CAPACITY}
              filteredTypeName={FILTER_CAPACITY_LOAD_HEIGHT}
              availableMinimum={
                availableCapacityFilters.loadHeight.min >= maxHeightVal
                  ? minHeightVal
                  : availableCapacityFilters.loadHeight.min
              }
              availableMaximum={
                availableCapacityFilters.loadHeight.max >= FILTER_CAPACITY_LOAD_HEIGHT_MAX
                  ? FILTER_CAPACITY_LOAD_HEIGHT_MAX
                  : availableCapacityFilters.loadHeight.max
              }
              activeMinimum={minHeightVal}
              activeMaximum={maxHeightVal}
              onChange={values => onCapacityChange(FILTER_CAPACITY_LOAD_HEIGHT, values)}
              onFinalChange={values => onCapacityFinalChange(FILTER_CAPACITY_LOAD_HEIGHT, values)}
              isLoading={isLoading}
            />
          </ComponentErrorBoundary>
        </div>
        <div className={styles.row}>
          <div className={styles.title}>
            <p>Load Width</p>
          </div>
          <ComponentErrorBoundary>
            <Range
              filter={FILTER_CAPACITY}
              filteredTypeName={FILTER_CAPACITY_LOAD_WIDTH}
              availableMinimum={
                availableCapacityFilters.loadWidth.min >= maxWidthVal
                  ? minWidthVal
                  : availableCapacityFilters.loadWidth.min
              }
              availableMaximum={
                availableCapacityFilters.loadWidth.max >= FILTER_CAPACITY_LOAD_WIDTH_MAX
                  ? FILTER_CAPACITY_LOAD_WIDTH_MAX
                  : availableCapacityFilters.loadWidth.max
              }
              activeMinimum={minWidthVal}
              activeMaximum={maxWidthVal}
              onChange={values => onCapacityChange(FILTER_CAPACITY_LOAD_WIDTH, values)}
              onFinalChange={values => onCapacityFinalChange(FILTER_CAPACITY_LOAD_WIDTH, values)}
              isLoading={isLoading}
            />
          </ComponentErrorBoundary>
        </div>
      </div>
    </FilterDropdown>
  )
}
